import { useState, useEffect, useCallback, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "../../lib/api";

// Compact daily bar chart for the last 30 days of a scraper's run history.
// Bars are stacked: green = ok, amber = partial, red = failed. Days with
// zero runs show a light gray "no runs" bar so gaps are visible.
// Complements the ScraperHealthBanner (which fires on 3+ consecutive
// failures) — this widget gives an at-a-glance history for spotting
// intermittent flakiness before it hits the alerting threshold.

export const ScraperReliabilityCard = ({
  scraperKey = "cruise_cruisemapper",
  title = "Cruise scraper reliability · last 30 days",
  testId = "scraper-reliability-cruise",
}) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/scraper-history", {
        params: { scraper: scraperKey, days: 30 },
      });
      setState({ loading: false, error: null, data: r.data });
    } catch (e) {
      setState({ loading: false, error: e.response?.data?.detail || "load failed", data: null });
    }
  }, [scraperKey]);

  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    const daily = state.data?.daily || [];
    return daily.map((row) => ({
      // Shorter axis label — "Jul 17" instead of full ISO
      date: new Date(row.run_date + "T12:00:00Z").toLocaleDateString(undefined, {
        month: "short", day: "numeric",
      }),
      ok: row.ok || 0,
      partial: row.partial || 0,
      failed: row.failed || 0,
      no_runs: (row.runs || 0) === 0 ? 1 : 0,
      rows_max: row.rows_written_max || 0,
    }));
  }, [state.data]);

  const summary = useMemo(() => {
    const runs = state.data?.runs || [];
    const ok = runs.filter((r) => r.status === "ok").length;
    const partial = runs.filter((r) => r.status === "partial").length;
    const failed = runs.filter((r) => r.status === "failed").length;
    const total = ok + partial + failed;
    return {
      total, ok, partial, failed,
      pct_ok: total > 0 ? ((ok / total) * 100).toFixed(1) : "—",
    };
  }, [state.data]);

  return (
    <div className="card-soft p-5" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280]">
          {title}
        </h3>
        {state.data && summary.total > 0 && (
          <span
            className="text-xs text-[#6B7280]"
            data-testid={`${testId}-summary`}
          >
            {summary.total} runs · <strong className="text-[#065F46]">{summary.pct_ok}%</strong> ok
            {summary.partial > 0 && <> · <span className="text-[#B45309]">{summary.partial} partial</span></>}
            {summary.failed > 0 && <> · <span className="text-[#B91C1C]">{summary.failed} failed</span></>}
          </span>
        )}
      </div>

      {state.loading ? (
        <div className="text-xs text-[#6B7280] py-8 text-center">Loading…</div>
      ) : state.error ? (
        <div className="text-xs text-[#B91C1C] py-8 text-center">
          Couldn't load history: {state.error}
        </div>
      ) : summary.total === 0 ? (
        <div className="text-xs text-[#6B7280] py-8 text-center" data-testid={`${testId}-empty`}>
          No scraper runs yet. First nightly run at 03:00 AST will populate this chart.
        </div>
      ) : (
        <div style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "#6B7280" }}
                interval={Math.max(0, Math.floor(chartData.length / 10) - 1)}
              />
              <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                labelStyle={{ color: "#1A1A1A", fontWeight: 500 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
              <Bar dataKey="ok" name="OK" stackId="a" fill="#22c55e" radius={[3, 3, 0, 0]} />
              <Bar dataKey="partial" name="Partial" stackId="a" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="failed" name="Failed" stackId="a" fill="#dc2626" radius={[3, 3, 0, 0]} />
              <Bar dataKey="no_runs" name="No runs" stackId="a" fill="#E5E7EB" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
